import React, { useEffect, useState, useRef, useCallback } from "react";
import { Container, Row, Col, Button, Spinner } from "reactstrap";
import "../../assets/css/catalog.css";
import { GetCatalogs } from "../../api/catalogService";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const Catalogs = () => {
    document.title = "Catalogs";

    const [allCatalogs, setAllCatalogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [imageLoaded, setImageLoaded] = useState({}); 
    const skeletonTimers = useRef({}); 


    const limit = 6; 
    const observer = useRef();

    useEffect(() => {
        fetchCatalogs();
    }, []);

    const fetchCatalogs = async () => {
        setLoading(true);
        try {
            const res = await GetCatalogs();
            const rows = Array.isArray(res?.ROW) ? res.ROW : [];
            const messages = Array.isArray(res?.ZMESSAGE) ? res.ZMESSAGE : [];
            const status = messages[0]?.RETURNMESSAGE || "No message";

            if (rows.length > 0) setAllCatalogs(rows);
            else setError(`No catalogs found. (${status})`);
        } catch (err) {
            console.error("Error fetching catalogs:", err);
            setError("Failed to load catalogs.");
        } finally {
            setLoading(false);
        }
    };

    const filteredCatalogs = allCatalogs.filter((cat) => {
        const title = (cat.TITLE || cat.WEBDISPLAYNAME || "").toLowerCase();
        return title.includes(search.toLowerCase());
    });

    const totalPages = Math.ceil(filteredCatalogs.length / limit);
    const currentCatalogs = filteredCatalogs.slice(0, page * limit);

    const lastCardRef = useCallback(
        (node) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && page < totalPages) {
                    setPage((prev) => prev + 1);
                }
            });

            if (node) observer.current.observe(node);
        },
        [loading, page, totalPages]
    );

    const handleImageLoad = (id) => {
        const start = skeletonTimers.current[id];
        const now = Date.now();
        const elapsed = start ? now - start : 0;
        const minDelay = 2000; 

        if (elapsed >= minDelay) {
            setImageLoaded((prev) => ({ ...prev, [id]: true }));
        } else {
            setTimeout(() => {
                setImageLoaded((prev) => ({ ...prev, [id]: true }));
            }, minDelay - elapsed);
        }
    };

    return (
        <Container className="my-5 text-center">
            <h1 className="mb-5 pb-3 fw-bold text-primary">Explore Catalogs</h1>

            <div className="search-wrapper d-flex justify-content-end mb-4">
                <div className="search-box">
                    <input
                        type="text"
                        className="form-control search-input"
                        placeholder="Search catalogs..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                    />

                    {search && (
                        <span
                            className="clear-icon"
                            onClick={() => {
                                setSearch("");
                                setPage(1);
                            }}
                        >
                            <i className="bx bx-x clear-icon"></i>
                        </span>
                    )}
                </div>
            </div>



            {loading && currentCatalogs.length === 0 && (
                <div className="text-center my-5">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading catalogs...</p>
                </div>
            )}

            {error && <div className="text-danger my-5">{error}</div>}

            <Row className="catalog-grid">
                {currentCatalogs.map((cat, idx) => {
                    const isLastItem = idx === currentCatalogs.length - 1;

                    // Use placeholder for PDFs or missing images
                    const imageSrc =
                        cat.ATTACHMENTTYPE !== "PDF" && cat.ATTACHMENTLINK
                            ? cat.ATTACHMENTLINK
                            : "/assets/images/binstock/no-image.jpg";

                    return (
                        <Col
                            key={`${cat.CATALOG_ID}-${cat.DETAIL_ID}-${idx}`}
                            className="p-2 col-12 col-sm-6 col-md-4 col-lg-4"
                        >
                            <div
                                className="catalog-card"
                                ref={isLastItem ? lastCardRef : null}
                            >
                                {/* Image Skeleton */}
                                {!imageLoaded[cat.CATALOG_ID] && (
                                    <Skeleton height={670} width="100%" />
                                )}

                                <img
                                    src={imageSrc}
                                    alt={cat.TITLE || cat.WEBDISPLAYNAME}
                                    className="catalog-img"
                                    style={{
                                        display: imageLoaded[cat.CATALOG_ID] ? "block" : "none",
                                    }}
                                    onLoad={() => handleImageLoad(cat.CATALOG_ID)}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "/assets/images/binstock/no-image.jpg";
                                        handleImageLoad(cat.CATALOG_ID);
                                    }}
                                />

                                <div className="catalog-overlay">
                                    <h6 className="catalog-title">
                                        {cat.TITLE || cat.WEBDISPLAYNAME}
                                    </h6>
                                    <Button
                                        color="primary"
                                        className="catalog-btn"
                                        onClick={() => window.open(cat.ATTACHMENTLINK, "_blank")}
                                    >
                                        {cat.ATTACHMENTTYPE === "PDF" ? "VIEW PDF" : "VIEW IMAGE"}
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    );
                })}
            </Row>

            {page < totalPages && (
                <div className="text-center my-3">
                    <Spinner color="primary" />
                    <p>Loading next page...</p>
                </div>
            )}
        </Container>
    );
};

export default Catalogs;
